import { useCallback, useState } from "react"
import { PilotTrackingTagMissionSpec } from "@nova/schemas"

export function usePendingTagState(missions: { missionId: string; tagIds: string[] }[]) {
    const [pendingAdds, setPendingAdds] = useState<Set<string>>(new Set())
    const [pendingRemoves, setPendingRemoves] = useState<Set<string>>(new Set())

    const getTagState = useCallback(
        (tagId: string): "applied" | "partial" | "not-applied" => {
            if (pendingAdds.has(tagId)) return "applied"
            if (pendingRemoves.has(tagId)) return "not-applied"
            const count = missions.filter((m) => m.tagIds.includes(tagId)).length
            if (count === missions.length) return "applied"
            if (count > 0) return "partial"
            return "not-applied"
        },
        [missions, pendingAdds, pendingRemoves]
    )

    const toggleTag = useCallback(
        (tagId: string) => {
            const isApplied =
                pendingAdds.has(tagId) ||
                (!pendingRemoves.has(tagId) && missions.every((m) => m.tagIds.includes(tagId)))
            if (isApplied) {
                setPendingAdds((prev) => {
                    const next = new Set(prev)
                    next.delete(tagId)
                    return next
                })
                setPendingRemoves((prev) => new Set(prev).add(tagId))
            } else {
                setPendingRemoves((prev) => {
                    const next = new Set(prev)
                    next.delete(tagId)
                    return next
                })
                setPendingAdds((prev) => new Set(prev).add(tagId))
            }
        },
        [pendingAdds, pendingRemoves, missions]
    )

    const buildDiff = useCallback(
        (
            additionalAddIds?: Set<string>
        ): {
            additions: PilotTrackingTagMissionSpec[]
            deletions: PilotTrackingTagMissionSpec[]
        } => {
            const additions: PilotTrackingTagMissionSpec[] = []
            const deletions: PilotTrackingTagMissionSpec[] = []
            const effectiveAdds =
                additionalAddIds != null
                    ? new Set([...pendingAdds, ...additionalAddIds])
                    : pendingAdds
            missions.forEach((mission) => {
                const current = new Set(mission.tagIds)
                effectiveAdds.forEach((tagId) => {
                    if (!current.has(tagId)) additions.push({ tagId, missionId: mission.missionId })
                })
                pendingRemoves.forEach((tagId) => {
                    if (current.has(tagId)) deletions.push({ tagId, missionId: mission.missionId })
                })
            })
            return { additions, deletions }
        },
        [missions, pendingAdds, pendingRemoves]
    )

    return { getTagState, toggleTag, buildDiff }
}
